export interface RateLimitInfo {
  remaining: number;
  resetTime: number;
  limit: number;
  isBlocked: boolean;
  blockReason?: string;
}

export interface UploadAttempt {
  userId: string;
  timestamp: number;
  fileSize: number;
  success: boolean;
}

export class UploadRateLimiter {
  private static readonly WINDOW_MS = 60 * 1000; // 1 minuto
  private static readonly MAX_UPLOADS_PER_MINUTE = 10;
  private static readonly MAX_FILE_SIZE_PER_MINUTE = 50 * 1024 * 1024; // 50MB por minuto
  private static readonly MAX_FAILED_ATTEMPTS = 5;
  private static readonly BLOCK_DURATION_MS = 15 * 60 * 1000; // 15 minutos

  private static uploadAttempts = new Map<string, UploadAttempt[]>();
  private static blockedUsers = new Map<string, { until: number; reason: string }>();

  /**
   * Verifica se usuário pode fazer upload
   */
  static canUpload(userId: string, fileSize: number): RateLimitInfo {
    // Verificar se usuário está bloqueado
    const blockInfo = this.blockedUsers.get(userId);
    if (blockInfo && Date.now() < blockInfo.until) {
      return {
        remaining: 0,
        resetTime: blockInfo.until,
        limit: 0,
        isBlocked: true,
        blockReason: blockInfo.reason
      };
    }

    // Limpar bloqueio se expirou
    if (blockInfo && Date.now() >= blockInfo.until) {
      this.blockedUsers.delete(userId);
    }

    const now = Date.now();
    const windowStart = now - this.WINDOW_MS;
    
    // Obter tentativas do usuário na janela atual
    const userAttempts = this.uploadAttempts.get(userId) || [];
    const recentAttempts = userAttempts.filter(attempt => attempt.timestamp >= windowStart);
    
    // Calcular limites
    const uploadsInWindow = recentAttempts.length;
    const sizeInWindow = recentAttempts.reduce((total, attempt) => total + attempt.fileSize, 0);
    
    const remainingUploads = Math.max(0, this.MAX_UPLOADS_PER_MINUTE - uploadsInWindow);
    const remainingSize = Math.max(0, this.MAX_FILE_SIZE_PER_MINUTE - sizeInWindow);
    
    // Verificar se pode fazer upload
    const canUpload = uploadsInWindow < this.MAX_UPLOADS_PER_MINUTE && 
                     sizeInWindow + fileSize <= this.MAX_FILE_SIZE_PER_MINUTE;

    return {
      remaining: Math.min(remainingUploads, Math.floor(remainingSize / fileSize)),
      resetTime: now + this.WINDOW_MS,
      limit: this.MAX_UPLOADS_PER_MINUTE,
      isBlocked: false
    };
  }

  /**
   * Registra tentativa de upload
   */
  static recordUploadAttempt(userId: string, fileSize: number, success: boolean): void {
    const now = Date.now();
    
    // Adicionar tentativa
    const userAttempts = this.uploadAttempts.get(userId) || [];
    userAttempts.push({
      userId,
      timestamp: now,
      fileSize,
      success
    });
    
    // Manter apenas tentativas da janela atual
    const windowStart = now - this.WINDOW_MS;
    const recentAttempts = userAttempts.filter(attempt => attempt.timestamp >= windowStart);
    
    this.uploadAttempts.set(userId, recentAttempts);
    
    // Verificar se deve bloquear usuário
    this.checkForBlocking(userId, recentAttempts);
    
    // Limpeza periódica de dados antigos
    this.cleanupOldData();
  }

  /**
   * Verifica se usuário deve ser bloqueado
   */
  private static checkForBlocking(userId: string, recentAttempts: UploadAttempt[]): void {
    const failedAttempts = recentAttempts.filter(attempt => !attempt.success);
    
    // Bloquear se muitas tentativas falharam
    if (failedAttempts.length >= this.MAX_FAILED_ATTEMPTS) {
      const blockUntil = Date.now() + this.BLOCK_DURATION_MS;
      this.blockedUsers.set(userId, {
        until: blockUntil,
        reason: `Muitas tentativas falharam (${failedAttempts.length}/${this.MAX_FAILED_ATTEMPTS})`
      });
      
      console.warn(`🚫 Usuário ${userId} bloqueado por ${this.BLOCK_DURATION_MS / 1000 / 60} minutos devido a falhas`);
    }
    
    // Bloquear se tentou fazer upload de arquivo muito grande
    const totalSize = recentAttempts.reduce((total, attempt) => total + attempt.fileSize, 0);
    if (totalSize > this.MAX_FILE_SIZE_PER_MINUTE * 2) { // 2x o limite
      const blockUntil = Date.now() + this.BLOCK_DURATION_MS;
      this.blockedUsers.set(userId, {
        until: blockUntil,
        reason: `Tamanho total excedeu limite (${this.formatBytes(totalSize)} > ${this.formatBytes(this.MAX_FILE_SIZE_PER_MINUTE)})`
      });
      
      console.warn(`🚫 Usuário ${userId} bloqueado por exceder limite de tamanho`);
    }
  }

  /**
   * Limpa dados antigos
   */
  private static cleanupOldData(): void {
    const now = Date.now();
    const cutoff = now - (this.WINDOW_MS * 2); // Manter 2x a janela para análise
    
    // Limpar tentativas antigas
    for (const [userId, attempts] of this.uploadAttempts.entries()) {
      const recentAttempts = attempts.filter(attempt => attempt.timestamp >= cutoff);
      if (recentAttempts.length === 0) {
        this.uploadAttempts.delete(userId);
      } else {
        this.uploadAttempts.set(userId, recentAttempts);
      }
    }
    
    // Limpar bloqueios expirados
    for (const [userId, blockInfo] of this.blockedUsers.entries()) {
      if (now >= blockInfo.until) {
        this.blockedUsers.delete(userId);
      }
    }
  }

  /**
   * Obtém estatísticas de rate limiting para um usuário
   */
  static getUserStats(userId: string): {
    attempts: UploadAttempt[];
    isBlocked: boolean;
    blockInfo?: { until: number; reason: string };
    limits: { uploads: number; size: number };
    usage: { uploads: number; size: number };
  } {
    const now = Date.now();
    const windowStart = now - this.WINDOW_MS;
    
    const userAttempts = this.uploadAttempts.get(userId) || [];
    const recentAttempts = userAttempts.filter(attempt => attempt.timestamp >= windowStart);
    
    const blockInfo = this.blockedUsers.get(userId);
    const isBlocked = blockInfo && now < blockInfo.until;
    
    return {
      attempts: recentAttempts,
      isBlocked,
      blockInfo: isBlocked ? blockInfo : undefined,
      limits: {
        uploads: this.MAX_UPLOADS_PER_MINUTE,
        size: this.MAX_FILE_SIZE_PER_MINUTE
      },
      usage: {
        uploads: recentAttempts.length,
        size: recentAttempts.reduce((total, attempt) => total + attempt.fileSize, 0)
      }
    };
  }

  /**
   * Remove bloqueio de um usuário
   */
  static unblockUser(userId: string): boolean {
    if (this.blockedUsers.has(userId)) {
      this.blockedUsers.delete(userId);
      console.log(`✅ Usuário ${userId} desbloqueado manualmente`);
      return true;
    }
    return false;
  }

  /**
   * Obtém estatísticas gerais do sistema
   */
  static getSystemStats(): {
    totalUsers: number;
    blockedUsers: number;
    totalAttempts: number;
    successRate: number;
  } {
    let totalAttempts = 0;
    let successfulAttempts = 0;
    
    for (const attempts of this.uploadAttempts.values()) {
      totalAttempts += attempts.length;
      successfulAttempts += attempts.filter(attempt => attempt.success).length;
    }
    
    return {
      totalUsers: this.uploadAttempts.size,
      blockedUsers: this.blockedUsers.size,
      totalAttempts,
      successRate: totalAttempts > 0 ? (successfulAttempts / totalAttempts) * 100 : 0
    };
  }

  /**
   * Formata bytes para leitura humana
   */
  private static formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Reseta todos os dados (útil para testes)
   */
  static reset(): void {
    this.uploadAttempts.clear();
    this.blockedUsers.clear();
    console.log('🔄 Rate limiter resetado');
  }
}
